import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * The console's button.
 *
 * Same brand recipe as the PWA's (design-guidelines §7) — the action gradient,
 * the `rounded-lg`, the universal focus ring — at desktop scale and, crucially,
 * NOT full-width by default. The PWA's `w-full` is right for a single-column
 * task screen and wrong in a toolbar, which is the clearest illustration of why
 * these two component sets are separate rather than shared.
 *
 * `h-control` is 36px. That is below the 44px NFR-05 target on purpose: it
 * applies to pointer-driven console chrome. Anything that must stay usable on
 * the mobile fallback takes `size="touch"` instead.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'touch';

/**
 * `success` and `danger` are the reactivate/deactivate pair.
 *
 * They exist as a PAIR because that is how they are read: in a master-data row
 * the same button position means "turn this on" or "turn this off" depending on
 * the row's state, and two ghost buttons that differ only in their label make
 * the reader parse text to find the destructive one. Green and red are the
 * reinforcement.
 *
 * Both keep their word (§1.3 — status is never colour alone), and both carry
 * white text on a fill that passes AA: `success #0B7A53` is 5.35:1 and
 * `danger #C0332B` is 5.60:1. The hover shades are the only hand-mixed values in
 * this file; they are one step darker than the token and are written as
 * arbitrary values so that fact is visible in review (§1.1).
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-gradient-to-br from-primary-700 to-primary-800 text-white ring-1 ring-inset ring-white/15 shadow-btn hover:from-primary-800 hover:to-primary-800 active:from-primary-800 active:to-primary-800',
  secondary:
    'border border-line bg-white text-primary-700 hover:bg-tint active:bg-tint',
  ghost: 'text-muted hover:bg-panel hover:text-ink active:bg-line',
  danger: 'bg-danger text-white hover:bg-[#A32C25] active:bg-[#A32C25]',
  success: 'bg-success text-white hover:bg-success-strong active:bg-success-strong',
};

const SIZES: Record<Size, string> = {
  sm: 'h-control-sm px-3 text-sm',
  md: 'h-control px-4 text-sm',
  touch: 'tap-target px-4 text-base',
};

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  className = '',
  ...rest
}: {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={`focus-ring inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-semibold transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
    >
      {children}
    </button>
  );
}
